import { InputAdornment } from '@mui/material';
import { clsx } from 'clsx';
import { useEffect, useState } from 'react';

import { useDebounce } from '../../../hooks/useDebounce';
import Icon from '../Icon';
import IconButton from '../IconButton';
import TextField from '../TextField';

import type { TextFieldProps } from '../TextField';
import type { FC } from 'react';

type SearchProps = TextFieldProps & {
  clearable?: boolean;
  iconPosition?: 'start' | 'end';
  search: string;
  setSearch: (value: string) => void;
  timeout?: number;
};

export const Search: FC<SearchProps> = ({
  className = '',
  clearable = false,
  iconPosition = 'end',
  InputProps,
  search,
  setSearch,
  timeout = 500,
  ...rest
}) => {
  const classes = clsx('mnt-search', className);
  const [value, setValue] = useState(search);

  useEffect(() => {
    setValue(search);
  }, [search]);

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);
  };

  useDebounce(value, setSearch, timeout);

  const searchAdornment = (
    <InputAdornment position={iconPosition}>
      <Icon>search</Icon>
    </InputAdornment>
  );
  const clearAdornment = (
    <InputAdornment position="end">
      <IconButton
        aria-label="Clear search"
        className="p-1"
        disabled={!value}
        size="small"
        onClick={() => {
          setValue('');
          setSearch('');
        }}
      >
        <Icon>close</Icon>
      </IconButton>
    </InputAdornment>
  );
  let endAdornment = InputProps?.endAdornment;
  if (iconPosition === 'end') endAdornment = searchAdornment;
  if (clearable) endAdornment = clearAdornment;

  return (
    <TextField
      {...rest}
      className={classes}
      InputProps={{
        ...InputProps,
        endAdornment,
        startAdornment:
          iconPosition === 'start'
            ? searchAdornment
            : InputProps?.startAdornment,
      }}
      label="Search..."
      value={value}
      onChange={onChange}
    />
  );
};

export default Search;
export type { SearchProps };
